import {observer} from 'mobx-react';
import {ObjectSelect2, Panel, Spinner} from 'shared/components';
import {Table} from 'main/components';
import useModal from 'shared/hooks/useModal';
import TimesheetEntry from 'main/stores/time_tracking/TimesheetEntry';
import {withState} from 'shared/core';
import TimesheetEditorState from '../state/TimesheetEditorState';
import {useIsMobile} from 'shared/hooks';
import EditEntryBreakModal from './EditEntryBreakModal';
import TimeClock from './TimeClock';
import EditEntryNoteModal from './EditEntryNoteModal';
import ClockInLocationModal from './ClockInLocationModal';
import Totals from './Totals';
import TimesheetActions from './TimesheetActions';
import StartTime from './StartTime';
import EndTime from './EndTime';
import Date from './Date';
import Note from './Note';
import Project from './Project';
import Hours from './Hours';
import ManualHours from './ManualHours';

const MANUAL_COLUMNS = [
  {
    header: 'time_tracking.timesheet.Date',
    width: 3,
    component: Date
  },
  {
    header: 'time_tracking.timesheet.Hours',
    width: 3,
    component: ManualHours
  },
  {
    header: 'time_tracking.timesheet.Project',
    width: 4.25,
    component: Project
  },
  {
    header: 'time_tracking.timesheet.Note',
    width: 0.75,
    component: Note
  }
];

const COLUMNS = [
  {
    header: 'time_tracking.timesheet.Date',
    width: 2,
    component: Date
  },
  {
    header: 'time_tracking.timesheet.In',
    width: 2,
    component: StartTime
  },
  {
    header: 'time_tracking.timesheet.Out',
    width: 2,
    component: EndTime
  },
  {
    header: 'time_tracking.timesheet.Hours',
    width: 1,
    component: Hours
  },
  {
    header: 'time_tracking.timesheet.Project',
    width: 3.25,
    component: Project
  },
  {
    header: 'time_tracking.timesheet.Note',
    width: 0.75,
    component: Note
  }
];

const TimesheetEditor = observer(({uiState, myTimesheet}) => {
  const editEntryNoteModalOpen = useModal(uiState, 'EditEntryNoteModal', 'editingEntry', TimesheetEntry);
  const editEntryBreakModalOpen = useModal(uiState, 'EditEntryBreakModal', 'editingEntry', TimesheetEntry);
  const clockInLocationModalOpen = useModal(uiState, 'ClockInLocationModal', 'viewingClockEvent');
  const {timesheet, payPeriod, payPeriods, employee, isLoadingEntries} = uiState;
  const isMobile = useIsMobile();

  if (isLoadingEntries) return <Panel><Spinner/></Panel>;

  return (
    <Panel.Stack loose>
      {timesheet.timeClockEnabled &&
        <TimeClock
          collectEmployeeLocation={timesheet.collectEmployeeLocation}
          onClockEvent={() => uiState.loadEntries()}
          employee={employee}
          myTimesheet={myTimesheet}
        />
      }
      <Panel>
        {payPeriods ? <div className='flex flex-wrap justify-content-between align-items-end mb3'>
          <ObjectSelect2
            value={payPeriod}
            items={payPeriods}
            display='effectiveDateView'
            label='time_tracking.timesheet.Pay Period'
            onChange={value => uiState.onPayPeriodSelected(value)}
          />
          <TimesheetActions uiState={uiState}/>
        </div> :
          <Panel.Header title={employee.name}>
            <TimesheetActions uiState={uiState}/>
          </Panel.Header>
        }
        <Totals uiState={uiState}/>
        <Table
          uiState={uiState}
          models={uiState.rows()}
          columns={uiState.timesheet.manuallyEnterHours ? MANUAL_COLUMNS : COLUMNS}
          customLinks={model => uiState.customLinksFor(model)}
          onlyShowCustomLinks
          onRemove={model => uiState.removeEntry(model.data)}
          horizontalScroll={isMobile}
        />
        <EditEntryNoteModal
          uiState={uiState}
          isOpen={editEntryNoteModalOpen}
        />
        <EditEntryBreakModal
          uiState={uiState}
          isOpen={editEntryBreakModalOpen}
        />
        <ClockInLocationModal
          uiState={uiState}
          isOpen={clockInLocationModalOpen}
        />
      </Panel>
    </Panel.Stack>
  );
});

export default withState(TimesheetEditor, TimesheetEditorState);
